import React, { Component, CSSProperties, ReactNode } from 'react';
import classnames from 'classnames';

import './Animated.scss';

interface Props {
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  isVisible: boolean;
  animationIn: AnimationName;
  animationOut: AnimationName;
  animationInDuration?: number;
  animationOutDuration?: number;
}

export type AnimationName = 'fadeIn' | 'fadeOut' | 'fadeInRight' | 'fadeOutRight';

const defaultDuration = 1000;

/**
 * Drop-in replacement for the `Animated` component from react-animated-css.
 *
 * That package was removed for two reasons. It declared `react: ^15.6.1` as a *regular*
 * dependency rather than a peer, so a complete React 15.7.0 copy sat in node_modules
 * next to our own — the classic source of invalid-hook-call bugs. And it drove its
 * animations with animate.css class names, which the app loaded from cdnjs on every
 * page view; nothing else used that stylesheet, so removing this removed the CDN too.
 *
 * The API is kept identical so the three call sites did not have to change shape, and
 * it stays a class component because Notification passes a callback `ref` to it as a
 * did-mount hook.
 *
 * Behaviour matches the original: children are always rendered, the animation plays on
 * every change of isVisible, and the final keyframe sticks (animate.css set
 * `animation-fill-mode: both`, which Animated.scss reproduces). Pointer events are
 * disabled while hidden so a faded-out panel cannot swallow clicks.
 */
export class Animated extends Component<Props> {

  public render() {
    const {
      children,
      className,
      style,
      isVisible,
      animationIn,
      animationOut,
      animationInDuration = defaultDuration,
      animationOutDuration = defaultDuration,
    } = this.props;

    const animation = isVisible ? animationIn : animationOut;
    const duration = isVisible ? animationInDuration : animationOutDuration;

    return (
      <div
        className={classnames('Animated', `Animated--${animation}`, className)}
        style={{
          animationDuration: `${duration}ms`,
          pointerEvents: isVisible ? 'all' : 'none',
          ...style,
        }}
      >
        {children}
      </div>
    );
  }
}
